import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, ThemeProvider, createTheme, Paper, CircularProgress, Divider, Chip, Rating } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import SearchForm from './components/SearchForm';
import Map from './components/Map';
import RankingsList from './components/RankingsList';

const theme = createTheme({
  palette: {
    primary: {
      main: '#4568dc',
    },
    secondary: {
      main: '#6f5de7',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h3: {
      fontWeight: 700,
      background: 'linear-gradient(45deg, #4568dc 0%, #6f5de7 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
});

function App() {
  const [searchResults, setSearchResults] = useState(null);
  const [center, setCenter] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [searchLocation, setSearchLocation] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchedBusinessDetails, setSearchedBusinessDetails] = useState(null);

  const handleBusinessClick = (business) => {
    setSelectedBusiness(business);
    setActiveMarker(business);
    // Center map on selected business
    if (business?.geometry?.location) {
      setCenter({
        lat: business.geometry.location.lat(),
        lng: business.geometry.location.lng()
      });
    }
  };
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [targetBusinessRank, setTargetBusinessRank] = useState(null);

  const clearResults = () => {
    setSearchResults(null);
    setSelectedBusiness(null);
    setCenter(null);
    setSearchKeyword('');
    setSearchLocation(null);
    setTargetBusinessRank(null);
  };

  useEffect(() => {
    if (window.google) {
      setIsLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => setIsLoaded(true);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleSearch = async ({ businessName, keyword, location, radius = 5000 }) => {
    try {
      clearResults();
      setIsSearching(true);
      setSearchKeyword(keyword);
      console.log('Searching for:', { businessName, keyword, location });
      
      // Store the initial business details that was searched for
      setSearchedBusinessDetails({
        name: businessName,
        keyword,
        location,
        radius
      });

      if (!isLoaded) {
        console.error('Google Maps not loaded');
        return;
      }

      const placesService = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );

      // Get the location coordinates from the provided address
      const geocoder = new window.google.maps.Geocoder();
      const geocodeResult = await new Promise((resolve, reject) => {
        geocoder.geocode({ 
          address: location,
          componentRestrictions: { country: 'IN' } // Restrict to India
        }, (results, status) => {
          if (status === 'OK') {
            setSearchLocation({
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
              address: results[0].formatted_address
            });
            resolve(results[0].geometry.location);
          } else reject(status);
        });
      });

      setCenter({
        lat: geocodeResult.lat(),
        lng: geocodeResult.lng()
      });

      // Get the keyword to search for
      const keywordToSearch = keyword.trim();
      console.log('Keyword to search for:', keywordToSearch, 'Radius:', radius);
      
      // Create an array to store all search results
      let allSearchResults = [];
      
      // Perform a search for the keyword
      const searchRequest = {
        query: keywordToSearch,
        location: geocodeResult,
        radius: radius, // Use the user-specified radius
        rankby: 'prominence', // Ensure we get the most prominent businesses within the radius
        region: 'in' // Restrict to India
      };

      const searchResults = await new Promise((resolve, reject) => {
        placesService.textSearch(searchRequest, (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            resolve(results);
          } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            resolve([]);
          } else {
            reject(status);
          }
        });
      });
      
      console.log(`Found ${searchResults.length} results for keyword: "${keywordToSearch}" within ${radius/1000}km`);
        
      // Add keyword information to each result
      const resultsWithKeyword = searchResults.map(place => ({
        ...place,
        searchKeyword: keywordToSearch,
        isTarget: place.name.toLowerCase().includes(businessName.toLowerCase())
      }));
      
      // Set the results
      allSearchResults = resultsWithKeyword;
      
      // If no results were found for any keyword, show an error
      if (allSearchResults.length === 0) {
        throw new Error('No results found for any of the provided keywords');
      }
      
      // Remove duplicates (same place_id)
      const uniqueResults = [];
      const seenPlaceIds = new Set();
      
      for (const result of allSearchResults) {
        if (!seenPlaceIds.has(result.place_id)) {
          seenPlaceIds.add(result.place_id);
          uniqueResults.push(result);
        }
      }
      
      console.log(`Found ${uniqueResults.length} unique results across all keywords`);
      
      // Use the unique results for further processing
      console.log(`Processing ${uniqueResults.length} search results`);
      
      // Function to calculate similarity between two strings (Levenshtein distance)
      const calculateSimilarity = (str1, str2) => {
        str1 = str1.toLowerCase();
        str2 = str2.toLowerCase();
        
        // Create a matrix
        const matrix = [];
        
        // Initialize the matrix
        for (let i = 0; i <= str1.length; i++) {
          matrix[i] = [i];
        }
        
        for (let j = 0; j <= str2.length; j++) {
          matrix[0][j] = j;
        }
        
        // Fill the matrix
        for (let i = 1; i <= str1.length; i++) {
          for (let j = 1; j <= str2.length; j++) {
            if (str1.charAt(i - 1) === str2.charAt(j - 1)) {
              matrix[i][j] = matrix[i - 1][j - 1];
            } else {
              matrix[i][j] = Math.min(
                matrix[i - 1][j - 1] + 1, // substitution
                matrix[i][j - 1] + 1,     // insertion
                matrix[i - 1][j] + 1      // deletion
              );
            }
          }
        }
        
        // Calculate similarity as a percentage (0 to 1)
        const maxLength = Math.max(str1.length, str2.length);
        const distance = matrix[str1.length][str2.length];
        return 1 - (distance / maxLength);
      };
      
      // Process results to identify the searched business
      const processedResults = uniqueResults.map(place => {
        // Calculate similarity score
        const similarityScore = calculateSimilarity(place.name, businessName);
        
        // Consider it a match if similarity is above 0.7 (70%)
        const isTarget = similarityScore > 0.7 || place.name.toLowerCase() === businessName.toLowerCase();
        
        return { ...place, isTarget, similarityScore };
      });

      console.log(`Total results after processing: ${processedResults.length}`);
      
      // Fetch additional details for each place to get complete address
      const getPlaceDetails = async (placeId) => {
        return new Promise((resolve, reject) => {
          placesService.getDetails(
            { placeId, fields: ['formatted_address', 'address_components', 'url'] },
            (result, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                resolve(result);
              } else {
                // Don't reject, just return null so we can continue with other places
                console.warn(`Could not get details for place ${placeId}: ${status}`);
                resolve(null);
              }
            }
          );
        });
      };
      
      // Get details for all places (in batches to avoid rate limits)
      const enhancedResults = [];
      for (let i = 0; i < processedResults.length; i++) {
        const place = processedResults[i];
        const isTarget = place.isTarget;
        
        try {
          // Get additional details for the place
          const details = await getPlaceDetails(place.place_id);
          
          enhancedResults.push({
            ...place,
            isTarget,
            formatted_address: details?.formatted_address || place.formatted_address || place.vicinity,
            url: details?.url
          });
        } catch (error) {
          console.warn(`Error fetching details for ${place.name}:`, error);
          enhancedResults.push({
            ...place,
            isTarget
          });
        }
      }

      const results = enhancedResults;

      // Sort by rating and review count
      const sortedResults = results.sort((a, b) => {
        const scoreA = (a.rating || 0) * Math.log(a.user_ratings_total || 1);
        const scoreB = (b.rating || 0) * Math.log(b.user_ratings_total || 1);
        return scoreB - scoreA;
      });

      // Find the rank of the target business
      const targetIndex = sortedResults.findIndex(place => place.isTarget);
      console.log('Target business found at index:', targetIndex);
      setTargetBusinessRank(targetIndex !== -1 ? targetIndex + 1 : null);

      setSearchResults(sortedResults);

      // Set the selected business to the target business
      const targetBusiness = sortedResults.find(b => b.isTarget);
      setSelectedBusiness(targetBusiness);
      
      // Update the searched business details with more information
      if (targetBusiness) {
        setSearchedBusinessDetails(prev => ({
          ...prev,
          ...targetBusiness,
          rank: targetIndex !== -1 ? targetIndex + 1 : null,
          totalResults: sortedResults.length
        }));
      }
      
      // Center the map on the target business if found, otherwise on the first result
      const centerBusiness = targetBusiness || sortedResults[0];
      if (centerBusiness) {
        setCenter({
          lat: centerBusiness.geometry.location.lat(),
          lng: centerBusiness.geometry.location.lng()
        });
      }
    } catch (error) {
      console.error('Error searching:', error);
      // Handle error appropriately
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Typography variant="h3" component="h1" gutterBottom align="center" sx={{
          mb: 4,
          fontSize: { xs: '2rem', md: '3rem' },
        }}>
          Echobooom - Local Rank Checker
        </Typography>
      
      <SearchForm onSearch={handleSearch} isSearching={isSearching} />
      
      {/* Loading indicator */}
      {isSearching && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress size={60} thickness={4} sx={{ color: '#4568dc' }} />
          <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="h6" color="primary">
              Searching for your business...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This may take a few moments
            </Typography>
          </Box>
        </Box>
      )}
      
      {/* Business details section */}
      {searchedBusinessDetails && !isSearching && (
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Rank badge */}
          {searchedBusinessDetails.rank && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 70,
              height: 70,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              clipPath: 'polygon(0 0, 100% 0, 100% 100%)',
              bgcolor: searchedBusinessDetails.rank <= 3 ? '#4caf50' : 
                       searchedBusinessDetails.rank <= 10 ? '#ff9800' : 
                       '#f44336',
              zIndex: 1
            }}>
              <Typography sx={{
                transform: 'rotate(45deg) translateY(-20px)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.2rem',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}>
                #{searchedBusinessDetails.rank}
              </Typography>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BusinessIcon sx={{ color: '#4568dc', mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {searchedBusinessDetails.name}
                </Typography>
                {searchedBusinessDetails.rank && (
                  <Chip 
                    label={`Rank #${searchedBusinessDetails.rank}`}
                    size="small"
                    color={searchedBusinessDetails.rank <= 3 ? 'success' : 
                           searchedBusinessDetails.rank <= 10 ? 'warning' : 
                           'error'}
                    sx={{ ml: 1, fontWeight: 'bold' }}
                  />
                )}
              </Box>
              
              {searchedBusinessDetails.formatted_address && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PlaceIcon sx={{ fontSize: 18, mr: 0.5, color: '#666' }} />
                  {searchedBusinessDetails.formatted_address}
                </Typography>
              )}
              
              {searchedBusinessDetails.rating && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                  <Rating
                    value={searchedBusinessDetails.rating}
                    readOnly
                    size="small"
                    precision={0.1}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {searchedBusinessDetails.rating} ({searchedBusinessDetails.user_ratings_total || 0} reviews)
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: { xs: 'flex-start', sm: 'flex-end' },
              justifyContent: 'center'
            }}>
              {searchedBusinessDetails.keywords && searchedBusinessDetails.keywords.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                    Searched for keywords:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                    {searchedBusinessDetails.keywords.map((kw, index) => (
                      <Chip 
                        key={index}
                        size="small"
                        label={kw}
                        sx={{ bgcolor: '#e8f0fe', color: '#1967d2' }}
                      />
                    ))}
                  </Box>
                </Box>
              ) : (
                <Chip 
                  icon={<SearchIcon />}
                  label={`Searched for: ${searchedBusinessDetails.keyword}`}
                  sx={{ mb: 1, bgcolor: '#e8f0fe', color: '#1967d2' }}
                />
              )}
              
              {searchedBusinessDetails.totalResults && (
                <Typography variant="body2" color="text.secondary">
                  Found {searchedBusinessDetails.totalResults} businesses in the area
                </Typography>
              )}
            </Box>
          </Box>
          

        </Paper>
      )}
      
      {searchResults && center && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {targetBusinessRank && (
            <Paper 
              elevation={3}
              sx={{ 
                p: 3, 
                background: 'linear-gradient(45deg, #4568dc 0%, #6f5de7 100%)',
                color: 'white',
                borderRadius: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Your Business Ranking
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    #{targetBusinessRank}
                    {targetBusinessRank <= 3 && '🏆'}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    {searchKeyword}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    {searchResults?.find(b => b.isTarget)?.vicinity}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>
            <RankingsList 
              businesses={searchResults}
              searchedBusiness={searchResults.find(b => b.isTarget)?.name || ''}
              searchKeyword={searchKeyword}
              onBusinessClick={handleBusinessClick}
              selectedBusiness={selectedBusiness}
            />
          </Box>
          <Box sx={{ flex: 1, minHeight: { xs: '400px', md: '600px' } }}>
            <Map
              center={center}
              businesses={searchResults}
              selectedBusiness={selectedBusiness}
              activeMarker={activeMarker}
              setActiveMarker={setActiveMarker}
              searchLocation={searchLocation}
            />
            </Box>
          </Box>
        </Box>
      )}
      <Box sx={{ 
        mt: 'auto', 
        pt: 3, 
        borderTop: '1px solid #e0e0e0',
        textAlign: 'center',
        fontSize: '0.875rem',
        color: 'text.secondary',
        py: 2
      }}>
        <Typography variant="body2" color="text.secondary">
          Disclaimer: All data presented in this application is fetched directly from Google Places API. 
          No made-up or artificially generated data is shown. Search results, rankings, and business information 
          are based on Google's data as of the time of search.
        </Typography>
      </Box>
    </Container>
    </ThemeProvider>
  );
}

export default App;
